/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef } from "react";
import { Container } from "react-grid-system";
import { TrackedLink } from "@components/analytics/TrackedLink";
import { VideoBox } from "@components/doc/VideoBox";
import Hero from "@components/layout/Hero";
import { ImgSrc } from "@config/image";
import theme from "@config/theme";

const DevCertificationPage = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isFirstLoadRef = useRef(true);

  // The iframe on this page is a multi-page Google Form. By default,
  // the form does _not_ scroll the user to the top of the form when
  // they click the "Next" button, and that kind of sucks.
  // This code listens for the "load" event on the iframe, which is
  // triggered any time the user clicks the "Next" or "Back" buttons,
  // and scrolls the user to top of the Assessment section.
  useEffect(() => {
    const handleLoad = () => {
      // Avoid scrolling to the top of the form when the page first loads
      if (isFirstLoadRef.current) {
        isFirstLoadRef.current = false;
        return;
      }

      const assessmentElement = document.getElementById("assessment");
      if (assessmentElement) {
        assessmentElement.scrollIntoView({ behavior: "smooth" });
      }
    };

    const iframe = iframeRef.current;
    if (iframe) {
      iframe.addEventListener("load", handleLoad);
    }

    return () => {
      if (iframe) {
        iframe.removeEventListener("load", handleLoad);
      }
    };
  }, []);

  return (
    <>
      <Hero
        title="Blues Developer Certification Program"
        subtitle="Test your knowledge of Blues and wireless connectivity to earn your Blues Developer Certification."
      />
      <Container>
        <section>
          <div>
            <img alt="" src={ImgSrc("/images/icons/requirements.svg")} />
          </div>
          <div>
            <h2>What is the Blues Developer Certification Program?</h2>
            <div className="intro-content">
              <div>
                <p>
                  This is an assessment path designed to validate expertise in
                  using Blues technologies (e.g. Notecard and Notehub) to add
                  wireless connectivity to disconnected products. For embedded
                  and/or IoT developers, getting certified demonstrates a &nbsp;
                  <strong>
                    command of device-to-cloud solutions, data connectivity, and
                    the Blues ecosystem
                  </strong>
                  .
                </p>
                <p>
                  The certification assessment is comprised of questions that
                  cover the following high-level topics:
                </p>
                <ol>
                  <li>A holistic look at the Blues ecosystem</li>
                  <li>Notecard, Notecarrier, and other Blues hardware</li>
                  <li>
                    Features of Notecard across Cellular, Wi-Fi, LoRa, and
                    Satellite
                  </li>
                  <li>Features of the Blues cloud service, Notehub</li>
                  <li>Capabilities of both Notecard and Notehub APIs</li>
                </ol>
              </div>
              <div>
                <img
                  alt="blues developer certification example"
                  src={ImgSrc("/images/community/dev-cert-template.jpg")}
                />
              </div>
            </div>
          </div>
        </section>

        <section>
          <div>
            <img alt="" src={ImgSrc("/images/icons/training.svg")} />
          </div>
          <div>
            <h2>Pre-Assessment Training</h2>
            <div>
              <div>
                <p>
                  Blues provides an extensive set of quickstarts, tutorials, and
                  accompanying video content to help you become certified.
                </p>
                <p>
                  The following resources will guide you from your first steps
                  with Blues, through to giving you the knowledge you need to
                  ace the assessment.
                </p>
                <p>
                  <i>
                    If you're already familiar with Notecard and Notehub, feel
                    free to &nbsp;
                    <a href="#assessment">jump straight to the assessment</a>.
                  </i>
                </p>
                <div className="get-started-container">
                  <div>
                    <h2>New to Blues?</h2>
                    <p>
                      Watch a recording of our "Getting Started with Blues"
                      webinar
                    </p>
                    <img
                      src={ImgSrc("/images/icons/curved-arrow.svg")}
                      alt=""
                    />
                  </div>
                  <div className="certification-video">
                    <VideoBox youtubeEmbedUrl="https://www.youtube.com/embed/FVhyaMxyA_E?si=u9IF0_gaMzN-YFlG" />
                  </div>
                </div>
                <h3 className="training-heading">
                  <img alt="1" src={ImgSrc("/images/icons/1.svg")} />
                  <p>Notecard Quickstart</p>
                </h3>
                <div className="training-content">
                  <p>
                    Learn how to connect to Notecard, how to set up Notecard's
                    companion cloud backend, Notehub, and how to start working
                    with data.
                  </p>
                  <TrackedLink href="/quickstart/notecard-quickstart">
                    Follow the guide
                  </TrackedLink>
                  &nbsp;or watch the video:
                  <div className="certification-video">
                    <VideoBox youtubeEmbedUrl="https://www.youtube.com/embed/81cEQ-dRMLM?si=GkEsgfvlYvjc3ryS" />
                  </div>
                </div>
                <h3 className="training-heading">
                  <img alt="2" src={ImgSrc("/images/icons/2.svg")} />
                  <p>Collecting Sensor Data</p>
                </h3>
                <div className="training-content">
                  <p>
                    Learn how to write firmware using the Notecard SDKs. You'll
                    collect temperature and humidity readings and send them to
                    the cloud at a regular interval.
                  </p>
                  <TrackedLink href="/guides-and-tutorials/collecting-sensor-data">
                    Follow the guide
                  </TrackedLink>
                  &nbsp;or watch the video:
                  <div className="certification-video">
                    <VideoBox youtubeEmbedUrl="https://www.youtube.com/embed/WS-DUPc-kZk?si=yaEXdqezhNwPe_-B" />
                  </div>
                </div>
                <h3 className="training-heading">
                  <img alt="3" src={ImgSrc("/images/icons/3.svg")} />
                  <p>Send Data to Your Cloud</p>
                </h3>
                <div className="training-content">
                  <p>
                    Learn about Notehub routes, which allow you to securely send
                    your data from Notehub to your cloud of choice.
                  </p>
                  <TrackedLink href="/guides-and-tutorials/routing-data-to-cloud">
                    Follow the guide
                  </TrackedLink>
                  &nbsp;or watch the video:
                  <div className="certification-video">
                    <VideoBox youtubeEmbedUrl="https://www.youtube.com/embed/Klxu8h21F6g?si=Lv84hHCQuradPR1e" />
                  </div>
                </div>
                <h3 className="training-heading">
                  <img alt="4" src={ImgSrc("/images/icons/4.png")} />
                  <p>Additional Notecard and Notehub Guides</p>
                </h3>
                <div className="training-content">
                  <p>
                    There are a myriad of other relevant guides and tutorials
                    available to help you understand the broader Blues
                    ecosystem. We recommend consulting a few of these before you
                    start your assessment.
                  </p>
                  <TrackedLink href="/guides-and-tutorials/notecard-guides/guide-listing">
                    See the Notecard guide listing
                  </TrackedLink>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="feedback">
          <div>
            <img alt="" src={ImgSrc("/images/icons/feedback.svg")} />
          </div>
          <div>
            <h2 id="assessment">Assessment</h2>

            <div className="form-container">
              <iframe
                ref={iframeRef}
                title="Blues Developer Certification Assessment"
                src="https://docs.google.com/forms/d/e/1FAIpQLScmWuokZadl9TDKUPg8y5slQxbGoisoU1PnWyYrasXl_9Cfcg/viewform?embedded=true"
                frameBorder={0}
                marginHeight={0}
                marginWidth={0}
              >
                Loading...
              </iframe>
            </div>
          </div>
        </section>
      </Container>
      <style jsx>
        {`
          .get-started-container {
            margin: 3rem auto 0 auto;
            display: flex;
          }
          .get-started-container h2 {
            margin-bottom: 1rem;
            font-size: 1.5rem;
          }
          .get-started-container p {
            margin: 0 0.25rem 0 0;
            max-width: 300px;
            font-size: 1.1rem;
          }
          .get-started-container img {
            display: block;
            margin: 0 auto;
          }

          .training-heading {
            display: flex;
            align-items: center;
            font-size: 1.5rem;
          }
          .training-heading img {
            margin-right: 10px;
            vertical-align: middle;
          }
          .training-heading p {
            margin: 0;
          }
          .training-content {
            padding-left: 60px;
          }

          .certification-video {
            width: 100%;
            max-width: 600px;
          }

          section {
            margin-top: 3rem;
            margin: 3rem auto 0 auto;
            max-width: 1000px;
            display: flex;
          }
          section li {
            margin: 0.5rem 0;
          }
          section h2 {
            line-height: 50px;
            margin-top: 0;
          }
          section img {
            display: block;
            margin-right: 1rem;
            height: 50px;
          }
          section ul {
            margin-left: 1rem;
            padding-left: 0;
          }
          section ul ul {
            margin-left: 2rem;
          }

          .intro-content {
            display: grid;
            grid-template-columns: 1.75fr 1fr;
            grid-gap: 1.5rem;
          }
          .intro-content p {
            margin-top: 0;
          }
          .intro-content > div > p:last-of-type {
            margin-bottom: 1.5rem;
          }
          .intro-content h3 {
            margin-top: 0;
          }
          .intro-content ul {
            list-style: none;
            padding: 0;
            margin: 0;
          }
          .intro-content li {
            padding-bottom: 0.5rem;
          }
          .intro-content img {
            margin-left: 0.5rem;
            height: auto;
            width: 400px;
            max-width: 100%;
            border-radius: 4px;
            box-shadow: ${theme.misc.boxShadowBlogImage};
          }

          .form-container {
            height: 2000px;
            padding-bottom: 0;
            max-width: 700px;
            width: 90vw;
          }
          .form-container iframe {
            width: 100%;
            height: 100%;
          }

          @media screen and (max-width: ${theme.breakpoints.md}px) {
            .intro-content {
              display: block;
            }
            .intro-content h3 {
              margin-top: 3rem;
            }
            .get-started-container {
              display: block;
            }
            .get-started-container p {
              font-size: 1rem;
              max-width: 100%;
              margin: 1rem 0;
            }
            .get-started-container img {
              display: none;
            }
            .form-container {
              max-width: 400px;
            }
          }

          @media screen and (max-width: ${theme.breakpoints.sm}px) {
            section > div > img {
              display: none;
            }
            .training-content {
              padding-left: 0;
            }
          }
        `}
      </style>
    </>
  );
};

export default DevCertificationPage;
